import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Grid3x3, List, Filter, X } from 'lucide-react';
import { fetchMilestones, deleteMilestone } from '../api/milestones';
import { getProjects } from '../api/projects';
import { getTasks } from '../api/tasks';
import { Milestone } from '../types/milestone';
import { Project } from '../types/project';
import { Task } from '../types/task';
import MilestoneForm from '../components/MilestoneForm';
import MilestoneCard from '../components/MilestoneCard';
import MilestoneTableView from '../components/MilestoneTableView';
import Button from '../components/Button';
import Card from '../components/Card';
import Select from '../components/Select';
import Breadcrumb from '../components/Breadcrumb';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { useAuth } from '../contexts/AuthContext';

const MilestonesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isTenantViewer, hasNoActiveTenant } = useAuth();
  // Read-only members of the workspace cannot create, and neither can a session with no
  // workspace (backend additionally enforces edit permission on the selected project).
  const canCreateMilestones = !!user && !isTenantViewer && !hasNoActiveTenant;
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | undefined>(undefined);
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    const saved = localStorage.getItem('milestonesView');
    return (saved === 'table' || saved === 'cards') ? saved : 'cards';
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [milestonesData, projectsData, tasksData] = await Promise.all([
        fetchMilestones(filterProjectId || undefined),
        getProjects(),
        getTasks(),
      ]);
      
      // Sort milestones chronologically by target_date
      const sortedMilestones = milestonesData.sort((a, b) => {
        return new Date(a.target_date).getTime() - new Date(b.target_date).getTime();
      });
      
      setMilestones(sortedMilestones);
      setProjects(projectsData);
      setTasks(tasksData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch milestones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjectId]);

  // Handle query parameters for project pre-selection and form auto-open
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('project');
    const shouldAdd = params.get('add') === 'true';

    if (projectId) {
      setFilterProjectId(projectId);
    }
    if (shouldAdd) {
      setShowForm(true);
    }

    // Clean URL after reading params
    if (projectId || shouldAdd) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Handle navigation state from project cards
  useEffect(() => {
    const state = location.state as { 
      projectId?: string;
      autoApply?: boolean;
    } | null;
    
    if (state?.projectId && state?.autoApply) {
      setFilterProjectId(state.projectId);
    }
  }, [location.state]);

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('milestonesView', viewMode);
  }, [viewMode]);

  const handleSuccess = (savedMilestone: Milestone) => {
    if (editingMilestone) {
      setMilestones(
        milestones.map((m) => (m.id === savedMilestone.id ? savedMilestone : m))
      );
      setEditingMilestone(undefined);
    } else {
      // Insert new milestone in chronological order
      const updatedMilestones = [...milestones, savedMilestone].sort((a, b) => {
        return new Date(a.target_date).getTime() - new Date(b.target_date).getTime();
      });
      setMilestones(updatedMilestones);
    }
    setShowForm(false);
  };

  const handleEdit = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setShowForm(true);
  };

  const handleDelete = async (milestone: Milestone) => {
    if (!window.confirm(`Delete milestone "${milestone.name}"? This will unset milestone_id for all associated tasks.`)) {
      return;
    }

    try {
      await deleteMilestone(milestone.id);
      setMilestones(milestones.filter((m) => m.id !== milestone.id));
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete milestone';
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMilestone(undefined);
  };

  const handleAddNew = () => {
    setEditingMilestone(undefined);
    setShowForm(true);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterProjectId(e.target.value);
  };

  const clearFilter = () => {
    setFilterProjectId('');
  };

  const handleViewTasks = (milestoneId: string) => {
    // Navigate to tasks page with milestone filter applied
    navigate('/tasks', { 
      state: { 
        filterType: 'milestone', 
        milestoneId, 
        autoApply: true 
      } 
    });
  };

  // Get task count for a milestone
  const getMilestoneTaskCount = (milestoneId: string): number => {
    return tasks.filter((t) => t.milestone_id === milestoneId).length;
  };

  // Get project name for a milestone
  const getProjectName = (projectId: string): string => {
    if (!projects || !Array.isArray(projects)) return `Project #${projectId}`;
    const project = projects.find((p) => p.id === projectId);
    return project ? project.name : `Project #${projectId}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-10 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      {/* Breadcrumb - only show when project filter active */}
      {filterProjectId && (
        <Breadcrumb
          items={[
            { label: 'Projects', path: '/projects' },
            { label: getProjectName(filterProjectId), path: '/projects' },
            { label: 'Milestones' }
          ]}
        />
      )}
      
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Milestones</h1>
          <p className="text-gray-900 dark:text-gray-100">Track project checkpoints and measure progress</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* View Toggle */}
          <div className="flex gap-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'cards' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={Grid3x3}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={List}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              Table
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Filter Controls */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-64">
          <Select
            label="Filter by Project"
            id="project-filter"
            value={filterProjectId}
            onChange={handleFilterChange}
          >
            <option value="">All Projects</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </div>

        {filterProjectId && (
          <Button variant="outline" onClick={clearFilter} className="mt-6">
            Clear Filter
          </Button>
        )}

        {canCreateMilestones && (
          <Button variant="primary" onClick={handleAddNew} className="mt-6">
            Add Milestone
          </Button>
        )}
      </div>

      {/* Filter Indicator */}
      {filterProjectId && (
        <div className="mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500 dark:border-primary-700 rounded-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <span className="font-semibold text-primary-900 dark:text-primary-100">
                Project: <strong>{getProjectName(filterProjectId)}</strong>
              </span>
            </div>
            <Button variant="outline" size="sm" leftIcon={X} onClick={clearFilter}>
              Clear Filter
            </Button>
          </div>
        </div>
      )}

      {/* Milestone Form */}
      {showForm && (
        <Card variant="glass" className="mb-6">
          <Card.Header
            title={editingMilestone ? 'Edit Milestone' : 'Create New Milestone'}
          />
          <Card.Body>
            <MilestoneForm
              milestone={editingMilestone}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </Card.Body>
        </Card>
      )}

      {/* Milestones List */}
      {milestones.length === 0 ? (
        <Card variant="glass">
          <Card.Body>
            <div className="text-center py-8">
              <p className="text-gray-900 dark:text-gray-100 mb-4">
                {filterProjectId
                  ? 'No milestones found for this project.'
                  : 'No milestones yet. Create your first milestone to get started!'}
              </p>
              <Button variant="primary" onClick={handleAddNew}>
                Create Milestone
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : viewMode === 'table' ? (
        <MilestoneTableView
          milestones={milestones}
          projects={projects}
          onView={(milestoneId) => navigate(`/milestones/${milestoneId}`)}
          onEdit={handleEdit}
          onDelete={(milestoneId) => {
            const milestone = milestones.find(m => m.id === milestoneId);
            if (milestone) handleDelete(milestone);
          }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {milestones.map((milestone) => (
            <MilestoneCard
              key={milestone.id}
              milestone={milestone}
              projectName={getProjectName(milestone.project_id)}
              taskCount={getMilestoneTaskCount(milestone.id)}
              onEdit={() => handleEdit(milestone)}
              onDelete={() => handleDelete(milestone)}
              onViewTasks={() => handleViewTasks(milestone.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MilestonesPage;
